'use client';

import { FC } from 'react';

import { Price } from '@ravago/shared/page-data/models/elements';
import { ShoppingCartItem } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { Link } from '../../../shared/link/link';

interface Props {
   shoppingCartItems: Array<ShoppingCartItem>;
   languageCode: string;
}

export const ShoppingCartOrderSummary: FC<Props> = ({ shoppingCartItems, languageCode }) => {
   const translate = useTranslations();
   const format = useFormatter();
   const items = shoppingCartItems.map((cartItem: ShoppingCartItem) => {
      const totalPricePerCartItem: Price = {
         amount: cartItem.unitPrice.price.amount * cartItem.quantity.amount,
         currency: cartItem.unitPrice.price.currency
      };
      return (
         <Box items="baseline" justify="between" key={cartItem.id}>
            <Typography color="subtle" component="span" variant="body">
               {cartItem.product.description}
            </Typography>
            <Typography align="right" component="span" variant="body-highlight">
               {format.price(totalPricePerCartItem)}
            </Typography>
         </Box>
      );
   });

   const hasProblems = shoppingCartItems.some((cartItem) => cartItem.problems.length > 0);

   const totalCartPrice: Price = {
      amount: shoppingCartItems.reduce((acc, cartItem) => acc + cartItem.unitPrice.price.amount * cartItem.quantity.amount, 0),
      currency: shoppingCartItems[0].unitPrice.price.currency
   };

   return (
      <Paper>
         <Box direction="column" gap="sm">
            <Typography component="h2" variant="heading-4">
               {translate('shopping-cart.summary')}
            </Typography>
         </Box>

         {items}

         <Divider />
         <Box justify="between" items="baseline">
            <Typography color="subtle" component="span" variant="body">
               {translate('shopping-cart.total-price')}
            </Typography>

            <Typography align="right" color="primary" component="h4" variant="body-highlight">
               {format.price(totalCartPrice)}
            </Typography>
         </Box>

         <Box items="center" direction="column" gap="xs">
            <Typography component="span" variant="caption">
               {translate('shopping-cart.terms-and-conditions')}
            </Typography>
            <Link fullWidth underline="none" color="light" href={`/${languageCode}/my-shopping-cart/checkout`} prefetch={false}>
               <Button disabled={hasProblems} fullWidth>
                  <Icon icon="arrow-right" /> {translate('shopping-cart.continue-to-checkout')}
               </Button>
            </Link>
         </Box>
      </Paper>
   );
};
